import React, { useEffect, useMemo, useState } from 'react';
import {
  AuditTrail, Callout, ColumnPicker, ConfirmDialog, DataTable, DensityToggle, EmptyState, Field, FilterBar,
  FormRow, ImageDropzone, NumberField, Pagination, SearchInput, SideDrawer, SummaryStrip, Toolbar,
  exportFilename, exportRows, formatThaiDate, ghostBtnCls, inputCls, primaryBtnCls,
  secondaryBtnCls, textareaCls, useTableDensity, useToast,
  type ActiveFilter, type AuditEntry, type Column, type ImageValue, type SortState, type SummaryStat,
} from '../components';
import { Icon } from '../components/base/Icon';
import {
  CURRENT_POINT_RULE, REWARDS, REWARD_TYPE_FIELDS, REWARD_TYPE_ICON, REWARD_TYPE_LABEL, spendFor,
  type Reward, type RewardFeature, type RewardType, type RewardVoucher,
} from './loyaltyData';

/**
 * RewardCatalogScreen — แคตตาล็อกของรางวัลและสต๊อก (FR-ADM-22)
 *
 * FR-RD-02 บังคับสามประเภท: สินค้าจริง · คูปองส่วนลด · สิทธิ์ฟรีค่าจัดส่ง
 * ทั้งสามประเภทใช้คะแนนแลกเหมือนกัน แต่ต้องกรอกข้อมูลไม่เหมือนกันเลย:
 * สินค้าจริงต้องมีสต๊อกและที่อยู่จัดส่ง คูปองต้องมีอายุหลังแลกและยอดขั้นต่ำ
 *
 * จึงตัดสินใจว่า **ประเภทเป็นตัวกำหนดว่าฟอร์มมีช่องอะไร** ไม่ใช่แสดงทุกช่องแล้วปล่อยว่าง
 * เพราะช่อง "อายุคูปองหลังแลก" ที่ปล่อยว่างในของรางวัลที่เป็นสินค้าจริง
 * จะกลายเป็นค่า null ที่ทีมพัฒนาต้องเดาความหมาย
 *
 * สองเรื่องที่หน้านี้ทำเป็นพิเศษ:
 *
 *   1. **สต๊อกใกล้หมดเตือนที่หัวหน้าจอ ไม่ใช่ในแถว** — ของรางวัลที่หมดสต๊อกแต่ยังเปิดให้แลก
 *      ทำให้ลูกค้าเสียคะแนนไปแล้วค้างในระบบ ซึ่งเป็นเรื่องร้องเรียนที่แก้ยากที่สุด
 *      หน้าจอจึงนับจำนวนรายการที่ใกล้หมดและหมดแล้วไว้บนสุด พร้อมปุ่มกรองไปดูทันที
 *
 *   2. **คะแนนแสดงคู่กับยอดซื้อที่ต้องใช้** — 1,200 คะแนนไม่บอกอะไรกับคนตั้งราคา
 *      แต่ "เท่ากับซื้อ 120,000 บาท" บอกทันทีว่าตั้งไว้แพงเกินจริง
 *      ตัวเลขนี้คำนวณจากกติกาคะแนนในหน้า PointRuleScreen — ถ้าอัตราเปลี่ยน ตัวเลขนี้เปลี่ยนตาม
 */

type ScreenState = 'ready' | 'loading' | 'error';

const stockState = (r: Reward): 'ok' | 'low' | 'out' | 'unlimited' => {
  if (r.stock === null) return 'unlimited';
  if (r.stock === 0) return 'out';
  if (r.stockAlertAt !== null && r.stock <= r.stockAlertAt) return 'low';
  return 'ok';
};

/** คอลัมน์ที่ต้องซ่อนเมื่อ backend ไม่รองรับส่วนนั้น */
const COLUMN_FEATURE: Record<string, RewardFeature> = {
  stock: 'stock',
  stockAlertAt: 'stock',
  limitPerPerson: 'limitPerPerson',
  redeemedTotal: 'redeemedTotal',
};

const NEW_VOUCHER: RewardVoucher = { kind: 'amount', value: null, maxDiscount: null };

/**
 * เหตุที่ยังบันทึกไม่ได้ — null = พร้อมบันทึก
 *
 * ตรวจที่หน้าจอก่อนเพราะ backend ตอบ "This field is required." เป็นภาษาอังกฤษ
 * และไม่บอกว่าเป็นช่องไหนในภาษาที่เจ้าหน้าที่ใช้
 */
const invalidReason = (r: Reward): string | null => {
  if (!r.name.trim()) return 'ต้องตั้งชื่อของรางวัลก่อน';
  if (r.type !== 'product' && r.couponValidDays === null) return 'ต้องตั้งอายุคูปองหลังแลก';
  if (r.type === 'coupon' && r.voucher) {
    if (r.voucher.value === null || r.voucher.value <= 0) return 'ต้องกรอกมูลค่าส่วนลด';
    if (r.voucher.kind === 'percent' && r.voucher.value > 100) return 'ส่วนลดเป็นเปอร์เซ็นต์ต้องไม่เกิน 100%';
    if (r.voucher.kind === 'percent' && r.voucher.maxDiscount === null) return 'ต้องตั้งเพดานส่วนลดเมื่อลดเป็นเปอร์เซ็นต์';
  }
  if (r.imageUrl && !r.imageAlt.trim()) return 'ต้องใส่คำอธิบายภาพ';
  return null;
};

const IMAGE_SPEC = {
  aspect: '1:1' as const,
  minWidth: 800,
  maxKB: 300,
  usedAt: 'การ์ดของรางวัลในแคตตาล็อก (ครอปเป็นสี่เหลี่ยมจัตุรัส) และหน้ารายละเอียดก่อนยืนยันการแลก',
};

const StockCell: React.FC<{ r: Reward }> = ({ r }) => {
  const s = stockState(r);
  if (s === 'unlimited') return <span className="text-[var(--ink-700)]">ไม่จำกัด</span>;
  const color = s === 'out' ? 'var(--error-text)' : s === 'low' ? 'var(--warning-text)' : 'var(--ink-900)';
  return (
    <span className="inline-flex items-center justify-end gap-[var(--space-15)]">
      <span className="bo-num font-[var(--weight-semibold)]" style={{ color }}>{r.stock!.toLocaleString('th-TH')}</span>
      {s === 'out' && (
        <span className="inline-flex items-center gap-1 text-[length:var(--text-table-sm)] text-[var(--error-text)]">
          <Icon name="production_quantity_limits" size={14} filled />หมดแล้ว
        </span>
      )}
      {s === 'low' && (
        <span className="inline-flex items-center gap-1 text-[length:var(--text-table-sm)] text-[var(--warning-text)]">
          <Icon name="warning" size={14} filled />ใกล้หมด
        </span>
      )}
    </span>
  );
};

export interface RewardCatalogScreenProps {
  initialState?: ScreenState;
  /** ของรางวัลจากชั้นข้อมูล — ไม่ส่งมา = ใช้ข้อมูลจำลอง */
  rewards?: Reward[];
  /** บันทึกจริง — ไม่ส่งมา = แก้ในเครื่องอย่างเดียว (โหมดสาธิต) */
  onSave?: (r: Reward) => Promise<void>;
  onDelete?: (r: Reward) => Promise<void>;
  onRefresh?: () => void;
  refreshing?: boolean;
  /**
   * ส่วนที่งานออกแบบมีแต่ backend ยังทำไม่ได้
   *
   * `key` ใช้ซ่อนคอลัมน์/ตัวกรอง/ช่องกรอก/ตัวเลขสรุปของส่วนนั้น · `label` แสดงเป็นคำเตือนบนหน้า
   * เพราะช่องที่กรอกได้แต่บันทึกไม่ขึ้นคือกับดักที่ร้ายกว่าการไม่มีช่องเลย
   */
  unsupportedFields?: Array<{ key: RewardFeature; label: string }>;
  /** true = ประเภทของรางวัลเปลี่ยนไม่ได้หลังสร้าง */
  lockTypeOnEdit?: boolean;
  /** คำเตือนเฉพาะประเภท แสดงในฟอร์มเมื่อเลือกประเภทนั้น */
  typeNotes?: Partial<Record<RewardType, string>>;
  /**
   * ประวัติการแก้ของรางวัลจาก AuditLog ของ backend
   * ไม่ส่งมา = ไม่แสดงส่วนประวัติ (โหมดดูหน้าจอโดยไม่มี backend)
   */
  auditEntries?: AuditEntry[];
}

export const RewardCatalogScreen: React.FC<RewardCatalogScreenProps> = ({
  auditEntries,
  initialState = 'ready',
  rewards: rewardsProp,
  onSave,
  onDelete,
  onRefresh,
  refreshing = false,
  unsupportedFields,
  lockTypeOnEdit = false,
  typeNotes,
}) => {
  const toast = useToast();
  const [screenState, setScreenState] = useState<ScreenState>(initialState);
  const [rows, setRows] = useState<Reward[]>(rewardsProp ?? REWARDS);

  /* ข้อมูลจากชั้น API มาทีหลัง — ต้องรับค่าใหม่เมื่อมันมาถึง
     เหมือนที่ทำในคิวคำขอ ไม่งั้นตารางค้างอยู่ที่ค่าตั้งต้นตลอดไป */
  useEffect(() => { if (rewardsProp) setRows(rewardsProp); }, [rewardsProp]);
  useEffect(() => { setScreenState(initialState); }, [initialState]);

  const hidden = useMemo(() => new Set((unsupportedFields ?? []).map((f) => f.key)), [unsupportedFields]);
  const shows = (f: RewardFeature) => !hidden.has(f);
  const [density, setDensity] = useTableDensity();

  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | RewardType>('all');
  const [stockFilter, setStockFilter] = useState<'all' | 'low' | 'out'>('all');
  const [enabledFilter, setEnabledFilter] = useState<'all' | 'on' | 'off'>('all');
  const [sort, setSort] = useState<SortState | null>(
    hidden.has('stock') ? { columnId: 'points', direction: 'asc' } : { columnId: 'stock', direction: 'asc' },
  );
  const [visibleCols, setVisibleCols] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const [edit, setEdit] = useState<Reward | null>(null);
  const [confirmSave, setConfirmSave] = useState<Reward | null>(null);

  const filtered = useMemo(() => rows.filter((r) => {
    if (typeFilter !== 'all' && r.type !== typeFilter) return false;
    if (stockFilter === 'low' && stockState(r) !== 'low') return false;
    if (stockFilter === 'out' && stockState(r) !== 'out') return false;
    if (enabledFilter === 'on' && !r.enabled) return false;
    if (enabledFilter === 'off' && r.enabled) return false;
    if (query && !r.name.includes(query.trim())) return false;
    return true;
  }), [rows, typeFilter, stockFilter, enabledFilter, query]);

  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const dir = sort.direction === 'asc' ? 1 : -1;
    const key = (r: Reward): string | number => {
      switch (sort.columnId) {
        case 'points': return r.points;
        case 'stock': return r.stock ?? Number.MAX_SAFE_INTEGER;
        case 'redeemedTotal': return r.redeemedTotal;
        case 'window': return r.to;
        case 'type': return REWARD_TYPE_LABEL[r.type];
        default: return r.name;
      }
    };
    return [...filtered].sort((a, b) => {
      const ka = key(a); const kb = key(b);
      return (typeof ka === 'number' && typeof kb === 'number' ? ka - kb : String(ka).localeCompare(String(kb), 'th')) * dir;
    });
  }, [filtered, sort]);

  const pageRows = sorted.slice((page - 1) * pageSize, page * pageSize);

  const allColumns: Array<Column<Reward>> = useMemo(() => [
    {
      id: 'name', header: 'ของรางวัล', width: 264, pinned: true, required: true, sortable: true,
      value: (r) => r.name,
      cell: (r) => (
        <span className="flex items-center gap-[var(--space-2)]">
          {r.imageUrl ? (
            <img src={r.imageUrl} alt="" className="h-[28px] w-[28px] shrink-0 rounded-[var(--radius-sm)] object-cover" />
          ) : (
            <span className="flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-[var(--radius-sm)] bg-[var(--surface-100)]">
              <Icon name="image_not_supported" size={15} className="text-[var(--ink-400)]" />
            </span>
          )}
          <span className="flex min-w-0 flex-col leading-[1.3]">
            <span className="bo-truncate font-[var(--weight-medium)] text-[var(--ink-900)]">{r.name}</span>
            {!r.enabled && <span className="text-[length:var(--text-table-sm)] text-[var(--ink-700)]">ปิดไม่ให้แลก</span>}
          </span>
        </span>
      ),
    },
    {
      id: 'type', header: 'ประเภท', width: 188, sortable: true,
      value: (r) => REWARD_TYPE_LABEL[r.type],
      cell: (r) => (
        <span className="inline-flex items-center gap-[var(--space-15)] text-[var(--ink-900)]">
          <Icon name={REWARD_TYPE_ICON[r.type]} size={16} className="text-[var(--blue-700)]" />
          <span className="bo-truncate">{REWARD_TYPE_LABEL[r.type]}</span>
        </span>
      ),
    },
    {
      id: 'points', header: 'คะแนนที่ใช้', width: 196, numeric: true, sortable: true,
      value: (r) => r.points,
      cell: (r) => (
        <span className="flex flex-col items-end leading-[1.3]">
          <span className="bo-num font-[var(--weight-semibold)]">{r.points.toLocaleString('th-TH')}</span>
          <span className="bo-num text-[length:var(--text-table-sm)] text-[var(--ink-700)]">
            = ซื้อ {spendFor(r.points, CURRENT_POINT_RULE).toLocaleString('th-TH')} บาท
          </span>
        </span>
      ),
    },
    { id: 'stock', header: 'สต๊อกคงเหลือ', width: 172, numeric: true, sortable: true, value: (r) => r.stock ?? '', cell: (r) => <StockCell r={r} /> },
    {
      id: 'stockAlertAt', header: 'เตือนเมื่อเหลือ', width: 128, numeric: true,
      value: (r) => r.stockAlertAt ?? '',
      cell: (r) => (r.stockAlertAt === null ? <span className="text-[var(--ink-400)]">—</span> : <span className="bo-num">{r.stockAlertAt.toLocaleString('th-TH')}</span>),
    },
    {
      id: 'window', header: 'ช่วงที่แลกได้', width: 176, sortable: true,
      value: (r) => `${r.from} – ${r.to}`,
      cell: (r) => (
        <span className="flex flex-col leading-[1.3] text-[length:var(--text-table-sm)]">
          <span className="bo-num" style={{ textAlign: 'left' }}>{formatThaiDate(r.from)}</span>
          <span className="bo-num text-[var(--ink-700)]" style={{ textAlign: 'left' }}>ถึง {formatThaiDate(r.to)}</span>
        </span>
      ),
    },
    {
      id: 'limitPerPerson', header: 'จำกัดต่อคน', width: 116, numeric: true,
      value: (r) => r.limitPerPerson ?? '',
      cell: (r) => (r.limitPerPerson === null ? <span className="text-[var(--ink-700)]">ไม่จำกัด</span> : <span className="bo-num">{r.limitPerPerson} ครั้ง</span>),
    },
    {
      id: 'couponValidDays', header: 'อายุคูปองหลังแลก', width: 148, numeric: true,
      value: (r) => r.couponValidDays ?? '',
      cell: (r) => (r.couponValidDays === null
        ? <span className="text-[var(--ink-400)]">ไม่ใช้กับประเภทนี้</span>
        : <span className="bo-num">{r.couponValidDays} วัน</span>),
    },
    { id: 'redeemedTotal', header: 'แลกไปแล้ว', width: 120, numeric: true, sortable: true, value: (r) => r.redeemedTotal, cell: (r) => <span className="bo-num">{r.redeemedTotal.toLocaleString('th-TH')}</span> },
    {
      id: 'conditions', header: 'เงื่อนไขการใช้', width: 300,
      value: (r) => r.conditions,
      cell: (r) => <span className="bo-truncate block text-[var(--ink-700)]" title={r.conditions}>{r.conditions}</span>,
    },
    {
      id: 'actions', header: 'จัดการ', width: 92, align: 'right', value: () => '',
      cell: (r) => (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setEdit(r); }}
          className="min-h-[var(--hit-table)] rounded-[var(--radius-sm)] px-[var(--space-2)] text-[length:var(--text-table-sm)] font-[var(--weight-medium)] text-[var(--blue-700)] underline underline-offset-2 outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
        >
          แก้ไข
        </button>
      ),
    },
  ], []);

  /**
   * ซ่อนคอลัมน์ของฟิลด์ที่ backend เก็บไม่ได้
   *
   * ⚠️ คอลัมน์ที่ว่างทุกแถวเจ้าหน้าที่อ่านว่า "ข้อมูลหาย" ไม่ใช่ "ระบบยังไม่รองรับ"
   *    ตัวเลขอย่าง "แลกไปแล้ว 0" อันตรายกว่า เพราะดูเหมือนข้อมูลจริงที่บอกว่าไม่มีใครแลก
   */
  const columns = useMemo(
    () => allColumns.filter((c) => {
      const f = COLUMN_FEATURE[c.id];
      return !f || !hidden.has(f);
    }),
    [allColumns, hidden],
  );


  const visible = visibleCols.length ? visibleCols : columns.map((c) => c.id);

  const totals = useMemo(() => ({
    active: rows.filter((r) => r.enabled).length,
    low: rows.filter((r) => stockState(r) === 'low').length,
    out: rows.filter((r) => stockState(r) === 'out').length,
    outAndOpen: rows.filter((r) => stockState(r) === 'out' && r.enabled).length,
    redeemed: rows.reduce((s, r) => s + r.redeemedTotal, 0),
    pointsBurned: rows.reduce((s, r) => s + r.redeemedTotal * r.points, 0),
  }), [rows]);

  const stats: SummaryStat[] = [
    { label: 'ของรางวัลที่เปิดให้แลก', value: totals.active.toLocaleString('th-TH') + ' รายการ', icon: 'redeem', tone: 'brand' },
    ...(shows('stock') ? [
      { label: 'สต๊อกใกล้หมด', value: totals.low.toLocaleString('th-TH') + ' รายการ', icon: 'warning', tone: totals.low ? 'warning' : 'default' },
      { label: 'สต๊อกหมดแล้ว', value: totals.out.toLocaleString('th-TH') + ' รายการ', icon: 'production_quantity_limits', tone: totals.out ? 'error' : 'default' },
    ] as SummaryStat[] : []),
    ...(shows('redeemedTotal') ? [
      { label: 'คะแนนที่ถูกใช้แลกไปแล้ว', value: totals.pointsBurned.toLocaleString('th-TH'), icon: 'savings' },
    ] as SummaryStat[] : []),
  ];

  const activeFilters: ActiveFilter[] = useMemo(() => {
    const out: ActiveFilter[] = [];
    if (typeFilter !== 'all') out.push({ id: 'type', field: 'ประเภท', display: REWARD_TYPE_LABEL[typeFilter] });
    if (stockFilter !== 'all') out.push({ id: 'stock', field: 'สต๊อก', display: stockFilter === 'low' ? 'ใกล้หมด' : 'หมดแล้ว' });
    if (enabledFilter !== 'all') out.push({ id: 'enabled', field: 'การเปิดให้แลก', display: enabledFilter === 'on' ? 'เปิด' : 'ปิด' });
    return out;
  }, [typeFilter, stockFilter, enabledFilter]);

  const clearAll = () => { setTypeFilter('all'); setStockFilter('all'); setEnabledFilter('all'); setQuery(''); setPage(1); };

  const doExport = (format: 'csv' | 'excel') => {
    const n = exportRows({
      columns, rows: sorted, columnIds: visible.filter((id) => id !== 'actions'),
      filename: exportFilename('แคตตาล็อกของรางวัล'), format,
    });
    toast.show({ tone: 'success', message: `ส่งออก ${n.toLocaleString('th-TH')} รายการแล้ว — บันทึกใน Audit Log` });
  };

  const save = async (r: Reward, reason?: string) => {
    const before = rows;
    setRows((prev) => (prev.some((x) => x.id === r.id)
      ? prev.map((x) => (x.id === r.id ? { ...r, provisional: false } : x))
      : [{ ...r, provisional: false }, ...prev]));
    setEdit(null);
    setConfirmSave(null);

    /* โหมดสาธิต — แก้ในเครื่องอย่างเดียว จึงเลิกทำได้ทันที */
    if (!onSave) {
      toast.show({
        tone: 'success',
        message: `บันทึก "${r.name}" แล้ว — บันทึกใน Audit Log`,
        undo: { onUndo: () => setRows(before), seconds: 10 },
      });
      void reason;
      return;
    }

    /**
     * ต่อ API จริง — ไม่มีปุ่ม "เลิกทำ"
     *
     * ⚠️ ตั้งใจไม่ใส่ เพราะการเลิกทำต้องยิง PATCH กลับซึ่งอาจล้มเหลวเงียบ ๆ
     *    ปุ่มที่กดแล้วอาจไม่ได้ผลแย่กว่าไม่มีปุ่ม
     *    ถ้าบันทึกไม่สำเร็จให้คืนค่าในตารางกลับ แล้วบอกตรง ๆ ว่าไม่สำเร็จ
     */
    try {
      await onSave(r);
      toast.show({ tone: 'success', message: `บันทึก "${r.name}" แล้ว` });
    } catch (err) {
      setRows(before);
      toast.show({
        tone: 'error',
        message: err instanceof Error ? err.message : `บันทึก "${r.name}" ไม่สำเร็จ`,
      });
    }
    void reason;
  };

  const remove = async (r: Reward) => {
    if (!onDelete) return;
    const before = rows;
    setRows((prev) => prev.filter((x) => x.id !== r.id));
    try {
      await onDelete(r);
      toast.show({ tone: 'success', message: `ลบ "${r.name}" แล้ว` });
    } catch (err) {
      setRows(before);
      toast.show({
        tone: 'error',
        message: err instanceof Error ? err.message : `ลบ "${r.name}" ไม่สำเร็จ`,
      });
    }
  };
  void remove;

  const has = (r: Reward, field: string) => REWARD_TYPE_FIELDS[r.type].includes(field);
  const isExisting = (r: Reward) => rows.some((x) => x.id === r.id);

  return (
    <div className="flex h-screen min-w-[var(--bo-min-width)] flex-col bg-[var(--surface-0)] font-[family-name:var(--font-thai)]">
      <Toolbar
        title="แคตตาล็อกของรางวัล"
        count={sorted.length}
        center={<SearchInput value={query} onChange={(v) => { setQuery(v); setPage(1); }} focusOnSlash placeholder="ค้นหาชื่อของรางวัล" width={300} />}
        end={
          <>
            <button
              type="button"
              className={primaryBtnCls}
              onClick={() => setEdit({
                id: `rw-${Date.now()}`, name: '', type: 'product', points: 1_000,
                stock: 100, stockAlertAt: 20, from: '2026-09-01', to: '2026-12-31',
                limitPerPerson: 1, couponValidDays: null, quantity: 1, conditions: '',
                imageUrl: null, imageAlt: '', enabled: false, redeemedTotal: 0, provisional: false,
              })}
            >
              <Icon name="add" size={17} />
              เพิ่มของรางวัล
            </button>
            {onRefresh && (
              <button
                type="button"
                onClick={onRefresh}
                disabled={refreshing}
                className={secondaryBtnCls}
              >
                <Icon name="refresh" size={17} className={refreshing ? 'animate-spin' : undefined} />
                {refreshing ? 'กำลังโหลด' : 'โหลดใหม่'}
              </button>
            )}
            <DensityToggle value={density} onChange={setDensity} />
            <ColumnPicker columns={columns} persistKey="rewards" value={visible} onChange={setVisibleCols} />
            <div className="flex items-center gap-[var(--space-05)] rounded-[var(--radius-sm)] border border-[var(--line-300)] p-[var(--space-05)]">
              <button type="button" onClick={() => doExport('excel')} className="inline-flex min-h-[var(--hit-icon-sm)] items-center gap-1 rounded-[var(--radius-xs)] px-[var(--space-15)] text-[length:var(--text-table-sm)] text-[var(--ink-700)] hover:bg-[var(--blue-050)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]">
                <Icon name="download" size={16} />Excel
              </button>
              <button type="button" onClick={() => doExport('csv')} className="inline-flex min-h-[var(--hit-icon-sm)] items-center rounded-[var(--radius-xs)] px-[var(--space-15)] text-[length:var(--text-table-sm)] text-[var(--ink-700)] hover:bg-[var(--blue-050)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]">CSV</button>
            </div>
          </>
        }
      />

      {/* ⚠️ ระหว่างโหลดยังไม่มีตัวเลขจริง — ซ่อนดีกว่าแสดงค่าที่อ่านเหมือนของจริง */}
      {screenState !== 'loading' && <SummaryStrip stats={stats} />}


      {screenState !== 'loading' && shows('stock') && (totals.outAndOpen > 0 || totals.low > 0) && (
        <div className="border-b border-[var(--line-200)] px-[var(--space-4)] py-[var(--space-2)]">
          <Callout
            tone={totals.outAndOpen > 0 ? 'error' : 'warning'}
            title={totals.outAndOpen > 0
              ? `${totals.outAndOpen} รายการสต๊อกหมดแล้วแต่ยังเปิดให้แลก`
              : `${totals.low} รายการสต๊อกใกล้หมด`}
            action={
              <button
                type="button"
                onClick={() => { setStockFilter(totals.outAndOpen > 0 ? 'out' : 'low'); setPage(1); }}
                className={secondaryBtnCls}
              >
                ดูรายการ
              </button>
            }
          >
            {totals.outAndOpen > 0
              ? 'ลูกค้าที่แลกของที่ไม่มีสต๊อกจะเสียคะแนนไปแล้วแต่ไม่ได้ของ ซึ่งต้องคืนคะแนนด้วยมือและเป็นเรื่องร้องเรียนที่แก้ยากที่สุด — ปิดไม่ให้แลกก่อน แล้วเปิดใหม่เมื่อของเข้า'
              : 'เมื่อสต๊อกหมดขณะยังเปิดให้แลก ลูกค้าจะเสียคะแนนไปแล้วแต่ไม่ได้ของ — เตรียมของเพิ่มหรือปิดไว้ก่อน'}
          </Callout>
        </div>
      )}

      <FilterBar
        active={activeFilters}
        onRemove={(id) => {
          if (id === 'type') setTypeFilter('all');
          if (id === 'stock') setStockFilter('all');
          if (id === 'enabled') setEnabledFilter('all');
          setPage(1);
        }}
        onClearAll={clearAll}
        currentFilters={{ typeFilter, stockFilter, enabledFilter }}
        persistKey="rewards"
        resultCount={sorted.length}
      >
        <label className="flex items-center gap-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-700)]">
            ประเภท
            <select value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value as typeof typeFilter); setPage(1); }} className={inputCls}>
              <option value="all">ทั้งสามประเภท</option>
              {(Object.keys(REWARD_TYPE_LABEL) as RewardType[]).map((t) => (
                <option key={t} value={t}>{REWARD_TYPE_LABEL[t]}</option>
              ))}
            </select>
        </label>
        {/* สต๊อกซ่อนพร้อมคอลัมน์ของมันเมื่อ backend ยังไม่รองรับ */}
        {shows('stock') && (
          <label className="flex items-center gap-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-700)]">
            สต๊อก
            <select value={stockFilter} onChange={(e) => { setStockFilter(e.target.value as typeof stockFilter); setPage(1); }} className={inputCls}>
              <option value="all">ทั้งหมด</option>
              <option value="low">ใกล้หมด</option>
              <option value="out">หมดแล้ว</option>
            </select>
          </label>
        )}
        <label className="flex items-center gap-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-700)]">
            การเปิดให้แลก
            <select value={enabledFilter} onChange={(e) => { setEnabledFilter(e.target.value as typeof enabledFilter); setPage(1); }} className={inputCls}>
              <option value="all">ทั้งหมด</option>
              <option value="on">เปิด</option>
              <option value="off">ปิด</option>
            </select>
        </label>
      </FilterBar>

      <DataTable<Reward>
        columns={columns.filter((c) => visible.includes(c.id))}
        rows={pageRows}
        rowKey={(r) => r.id}
        caption={`แคตตาล็อกของรางวัล — ${sorted.length} รายการ`}
        state={screenState}
        rowCountHint={9}
        errorMessage="โหลดแคตตาล็อกของรางวัลไม่สำเร็จ — เชื่อมต่อเซิร์ฟเวอร์ไม่ได้"
        onRetry={() => setScreenState('ready')}
        emptyState={activeFilters.length || query
          ? <EmptyState kind="no-results" title="ไม่พบของรางวัลที่ตรงกับตัวกรอง" action={{ label: 'ล้างตัวกรองทั้งหมด', onClick: clearAll }} />
          : <EmptyState kind="no-data" title="ยังไม่มีของรางวัล" description="เพิ่มของรางวัลชิ้นแรกเพื่อให้สมาชิกเริ่มแลกคะแนนได้" />}
        sort={sort}
        onSortChange={setSort}
        onOpenRow={setEdit}
        density={density}
        persistKey="rewards"
        isFlagged={(r) => stockState(r) === 'out' && r.enabled}
        className="min-h-0 flex-1 rounded-none border-0"
      />

      <Pagination
        page={page}
        pageSize={pageSize}
        totalItems={sorted.length}
        onPageChange={setPage}
        onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
      />

      {/* -------------------------------------------------- แก้ไข / เพิ่ม */}
      <SideDrawer
        open={Boolean(edit)}
        onClose={() => setEdit(null)}
        title={rows.some((x) => x.id === edit?.id) ? 'แก้ไขของรางวัล' : 'เพิ่มของรางวัล'}
        subtitle={edit?.name || 'กรอกประเภทก่อน — ช่องที่ต้องกรอกจะเปลี่ยนตามประเภท'}
        width="lg"
        footer={edit && (
          <div className="flex flex-wrap items-center gap-[var(--space-2)]">
            <button
              type="button"
              onClick={() => setConfirmSave(edit)}
              disabled={invalidReason(edit) !== null}
              className={primaryBtnCls}
            >
              บันทึก
            </button>
            <button type="button" onClick={() => setEdit(null)} className={secondaryBtnCls}>ยกเลิก</button>
            {invalidReason(edit) && (
              <span className="inline-flex items-center gap-1 text-[length:var(--text-table-sm)] text-[var(--error-text)]">
                <Icon name="error" size={15} filled />{invalidReason(edit)}
              </span>
            )}
          </div>
        )}
      >
        {edit && (() => {
          const s = stockState(edit);
          const typeLocked = lockTypeOnEdit && isExisting(edit);
          const typeNote = typeNotes?.[edit.type];
          return (
            <div className="flex flex-col gap-[var(--space-4)]">
              {s === 'out' && edit.enabled && (
                <Callout tone="error" title="สต๊อกหมดแล้วแต่ยังเปิดให้แลก">
                  ปิดไม่ให้แลกก่อน แล้วเปิดใหม่เมื่อของเข้า — ไม่งั้นลูกค้าจะเสียคะแนนไปโดยไม่ได้ของ
                  <span className="mt-[var(--space-2)] block">
                    <button type="button" onClick={() => setEdit({ ...edit, enabled: false })} className={secondaryBtnCls}>
                      ปิดไม่ให้แลกทันที
                    </button>
                  </span>
                </Callout>
              )}

              <FormRow label="ชื่อของรางวัล" required help="ข้อความนี้ลูกค้าเห็นในแคตตาล็อก">
                <input
                  data-autofocus
                  type="text"
                  value={edit.name}
                  onChange={(e) => setEdit({ ...edit, name: e.target.value })}
                  className={`${inputCls} w-full`}
                  placeholder="เช่น Innobic Multivitamin 30 เม็ด"
                />
              </FormRow>

              <FormRow
                label="ประเภท"
                required
                help={typeLocked
                  ? 'ประเภทเปลี่ยนไม่ได้หลังสร้าง — ถ้าต้องการประเภทอื่นให้เพิ่มของรางวัลใหม่ แล้วปิดชิ้นนี้ไม่ให้แลก'
                  : 'ประเภทเป็นตัวกำหนดว่าต้องกรอกอะไรต่อ'}
              >
                <div className="flex flex-col gap-[var(--space-15)]">
                  {(Object.keys(REWARD_TYPE_LABEL) as RewardType[]).map((t) => (
                    <label
                      key={t}
                      className={`flex items-start gap-[var(--space-2)] rounded-[var(--radius-sm)] border border-[var(--line-200)] p-[var(--space-2)] ${
                        typeLocked && edit.type !== t ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-[var(--blue-050)]'
                      }`}
                    >
                      <input
                        type="radio"
                        name="rewardType"
                        checked={edit.type === t}
                        disabled={typeLocked && edit.type !== t}
                        onChange={() => setEdit({
                          ...edit,
                          type: t,
                          stock: t === 'product' ? (edit.stock ?? 100) : edit.stock,
                          couponValidDays: t === 'product' ? null : (edit.couponValidDays ?? 30),
                          voucher: t === 'coupon' ? (edit.voucher ?? NEW_VOUCHER) : edit.voucher,
                          minSpend: t === 'product' ? edit.minSpend : (edit.minSpend ?? 0),
                        })}
                        className="mt-[3px] h-4 w-4 shrink-0 accent-[var(--blue-700)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
                      />
                      <span className="min-w-0">
                        <span className="flex items-center gap-[var(--space-15)] text-[length:var(--text-form-lg)] text-[var(--ink-900)]">
                          <Icon name={REWARD_TYPE_ICON[t]} size={18} className="text-[var(--blue-700)]" />
                          {REWARD_TYPE_LABEL[t]}
                        </span>
                        <span className="block text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-700)]">
                          {t === 'product' && 'ต้องมีสต๊อกและที่อยู่จัดส่งของลูกค้า สถานะการจัดส่งแสดงในประวัติการแลก'}
                          {t === 'coupon' && 'ออกรหัสคูปองให้ลูกค้าทันที ต้องตั้งอายุคูปองหลังแลกและเงื่อนไขการใช้'}
                          {t === 'shipping' && 'สิทธิ์ยกเว้นค่าจัดส่ง ใช้ครั้งเดียวต่อคำสั่งซื้อ ต้องตั้งอายุหลังแลก'}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              </FormRow>

              {typeNote && <Callout tone="warning" title="ข้อจำกัดของประเภทนี้">{typeNote}</Callout>}

              <div className="grid grid-cols-2 gap-[var(--space-3)]">
                <FormRow label="คะแนนที่ใช้แลก" required>
                  <NumberField value={edit.points} onChange={(v) => setEdit({ ...edit, points: v === '' ? 0 : v })} unit="คะแนน" min={1} step={50} width={168} ariaLabel="คะแนนที่ใช้แลก" />
                </FormRow>
                <Field
                  label="เท่ากับยอดซื้อ"
                  hint={`คำนวณจากอัตรา ${CURRENT_POINT_RULE.spendPerPoint} บาท = ${CURRENT_POINT_RULE.pointsEarned} คะแนน ที่ตั้งไว้ในหน้ากติกาคะแนน`}
                >
                  <span className="bo-num" style={{ textAlign: 'left' }}>
                    {spendFor(edit.points, CURRENT_POINT_RULE).toLocaleString('th-TH')} บาท
                  </span>
                </Field>
              </div>

              {has(edit, 'stock') && shows('stock') && (
                <section className="flex flex-col gap-[var(--space-3)] rounded-[var(--radius-md)] border border-[var(--line-200)] p-[var(--space-3)]">
                  <h3 className="m-0 text-[length:var(--text-form-lg)] font-[var(--weight-semibold)] text-[var(--ink-900)]">สต๊อกและการแจ้งเตือน</h3>
                  <div className="flex flex-wrap items-end gap-[var(--space-4)]">
                    <FormRow label="สต๊อกคงเหลือ">
                      <span className="flex items-center gap-[var(--space-2)]">
                        <NumberField
                          value={edit.stock ?? ''}
                          onChange={(v) => setEdit({ ...edit, stock: v === '' ? null : v })}
                          unit="ชิ้น" min={0} width={148}
                          disabled={edit.stock === null}
                          ariaLabel="สต๊อกคงเหลือ"
                        />
                        <button
                          type="button"
                          onClick={() => setEdit({ ...edit, stock: edit.stock === null ? 100 : null, stockAlertAt: edit.stock === null ? 20 : null })}
                          className={secondaryBtnCls}
                        >
                          {edit.stock === null ? 'ตั้งสต๊อก' : 'ไม่จำกัด'}
                        </button>
                      </span>
                    </FormRow>
                    {edit.stock !== null && (
                      <FormRow label="แจ้งเตือนเมื่อเหลือ" help="หน้าจอจะขึ้นเตือนที่ด้านบนและส่งแจ้งเตือนถึงผู้ดูแลสต๊อก">
                        <NumberField
                          value={edit.stockAlertAt ?? ''}
                          onChange={(v) => setEdit({ ...edit, stockAlertAt: v === '' ? null : v })}
                          unit="ชิ้น" min={0} width={148} ariaLabel="เตือนเมื่อสต๊อกเหลือ"
                        />
                      </FormRow>
                    )}
                  </div>
                  {edit.stock !== null && edit.stockAlertAt !== null && edit.stockAlertAt >= edit.stock && (
                    <Callout tone="warning">
                      ค่าที่ตั้งเตือน ({edit.stockAlertAt.toLocaleString('th-TH')}) มากกว่าหรือเท่ากับสต๊อกที่มี
                      ({edit.stock.toLocaleString('th-TH')}) — การเตือนจะขึ้นทันทีและขึ้นค้างไว้ตลอด
                    </Callout>
                  )}
                  <p className="m-0 text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-700)]">
                    <Icon name="link" size={16} className="mr-1 inline align-[-3px]" />
                    สต๊อกของรางวัลแยกจากสต๊อกสินค้าเพื่อการขาย
                  </p>
                </section>
              )}

              {has(edit, 'couponValue') && !edit.voucher && isExisting(edit) && (
                <Callout tone="info" title="มูลค่าคูปองแก้จากหน้านี้ไม่ได้">
                  ของรางวัลนี้ออกคูปองชนิดที่หน้านี้ยังไม่รองรับ — ค่าเดิมจะไม่ถูกเปลี่ยนเมื่อบันทึก
                </Callout>
              )}

              {has(edit, 'couponValue') && edit.voucher && (() => {
                const v = edit.voucher;
                const setV = (patch: Partial<RewardVoucher>) => setEdit({ ...edit, voucher: { ...v, ...patch } });
                return (
                  <section className="flex flex-col gap-[var(--space-3)] rounded-[var(--radius-md)] border border-[var(--line-200)] p-[var(--space-3)]">
                    <h3 className="m-0 text-[length:var(--text-form-lg)] font-[var(--weight-semibold)] text-[var(--ink-900)]">มูลค่าคูปอง</h3>
                    <FormRow label="ลดแบบ" required>
                      <span className="flex flex-wrap gap-[var(--space-3)]">
                        {([['amount', 'ลดเป็นบาท'], ['percent', 'ลดเป็นเปอร์เซ็นต์']] as const).map(([k, label]) => (
                          <label key={k} className="flex cursor-pointer items-center gap-[var(--space-15)] text-[length:var(--text-form-lg)] text-[var(--ink-900)]">
                            <input
                              type="radio"
                              name="voucherKind"
                              checked={v.kind === k}
                              onChange={() => setV({ kind: k, maxDiscount: k === 'amount' ? null : v.maxDiscount })}
                              className="h-4 w-4 shrink-0 accent-[var(--blue-700)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
                            />
                            {label}
                          </label>
                        ))}
                      </span>
                    </FormRow>
                    <div className="flex flex-wrap items-end gap-[var(--space-4)]">
                      <FormRow label="มูลค่าส่วนลด" required>
                        <NumberField
                          value={v.value ?? ''}
                          onChange={(n) => setV({ value: n === '' ? null : n })}
                          unit={v.kind === 'percent' ? '%' : 'บาท'}
                          min={1} max={v.kind === 'percent' ? 100 : undefined} width={160}
                          ariaLabel="มูลค่าส่วนลด"
                        />
                      </FormRow>
                      {v.kind === 'percent' && (
                        <FormRow label="ลดสูงสุด" required help="เพดานส่วนลดต่อคำสั่งซื้อ">
                          <NumberField
                            value={v.maxDiscount ?? ''}
                            onChange={(n) => setV({ maxDiscount: n === '' ? null : n })}
                            unit="บาท" min={1} width={160} ariaLabel="เพดานส่วนลด (บาท)"
                          />
                        </FormRow>
                      )}
                    </div>
                  </section>
                );
              })()}

              {has(edit, 'minSpend') && (
                <FormRow label="ยอดซื้อขั้นต่ำที่ใช้คูปองได้" help="0 = ไม่มีขั้นต่ำ">
                  <NumberField
                    value={edit.minSpend ?? 0}
                    onChange={(n) => setEdit({ ...edit, minSpend: n === '' ? 0 : n })}
                    unit="บาท" min={0} width={168} ariaLabel="ยอดซื้อขั้นต่ำ (บาท)"
                  />
                </FormRow>
              )}

              {has(edit, 'couponValidDays') && (
                <section className="flex flex-col gap-[var(--space-3)] rounded-[var(--radius-md)] border-2 border-[var(--blue-200)] p-[var(--space-3)]">
                  <h3 className="m-0 flex items-center gap-[var(--space-2)] text-[length:var(--text-form-lg)] font-[var(--weight-semibold)] text-[var(--ink-900)]">
                    <Icon name="event_busy" size={19} filled className="text-[var(--blue-700)]" />
                    อายุคูปองหลังแลก
                  </h3>
                  <p className="m-0 text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-700)]">
                    นับจากวันที่ลูกค้าแลก ไม่ใช่จากวันที่แคมเปญเริ่ม — คูปองที่หมดอายุก่อนใช้ทำให้ลูกค้าเสียคะแนนไปเปล่า
                    ระบบต้องแจ้งเตือนก่อนหมดอายุตามค่าที่ตั้งในหน้ากติกาคะแนน
                  </p>
                  <div className="flex flex-wrap items-end gap-[var(--space-4)]">
                    <FormRow label="ใช้ได้ภายใน" required>
                      <NumberField
                        value={edit.couponValidDays ?? ''}
                        onChange={(v) => setEdit({ ...edit, couponValidDays: v === '' ? null : v })}
                        unit="วันหลังแลก" min={1} width={192} ariaLabel="อายุคูปองหลังแลก (วัน)"
                      />
                    </FormRow>
                    <span className="flex flex-wrap gap-[var(--space-15)] pb-[var(--space-1)]">
                      {[14, 30, 60, 90].map((d) => (
                        <button key={d} type="button" onClick={() => setEdit({ ...edit, couponValidDays: d })} className={ghostBtnCls}>
                          {d} วัน
                        </button>
                      ))}
                    </span>
                  </div>
                  {edit.couponValidDays !== null && edit.couponValidDays < 14 && (
                    <Callout tone="warning">
                      อายุสั้นกว่า 14 วันทำให้ลูกค้าที่แลกไว้ล่วงหน้าใช้ไม่ทัน — ดูอัตราคูปองที่หมดอายุโดยไม่ได้ใช้
                      ในรายงานการแลกของรางวัลก่อนตั้งค่าต่ำกว่านี้
                    </Callout>
                  )}
                  <FormRow label="ได้คูปองกี่ใบต่อการแลกหนึ่งครั้ง" help="ไม่ใช่จำนวนคูปองทั้งหมด — แลกหนึ่งครั้งได้คูปองตามจำนวนนี้">
                    <NumberField
                      value={edit.quantity ?? 1}
                      onChange={(n) => setEdit({ ...edit, quantity: n === '' ? 1 : n })}
                      unit="ใบ" min={1} width={148} ariaLabel="จำนวนคูปองต่อการแลก"
                    />
                  </FormRow>
                </section>
              )}

              <div className="grid grid-cols-2 gap-[var(--space-3)]">
                <FormRow label="เริ่มแลกได้">
                  <input type="date" value={edit.from} onChange={(e) => setEdit({ ...edit, from: e.target.value })} className={`${inputCls} bo-num`} />
                </FormRow>
                <FormRow label="แลกได้ถึง">
                  <input type="date" value={edit.to} onChange={(e) => setEdit({ ...edit, to: e.target.value })} className={`${inputCls} bo-num`} />
                </FormRow>
              </div>

              {shows('limitPerPerson') && (
              <FormRow label="จำกัดกี่ครั้งต่อคน" help="นับตลอดช่วงที่แลกได้ ไม่ใช่ต่อปี">
                <span className="flex items-center gap-[var(--space-2)]">
                  <NumberField
                    value={edit.limitPerPerson ?? ''}
                    onChange={(v) => setEdit({ ...edit, limitPerPerson: v === '' ? null : v })}
                    unit="ครั้ง" min={1} width={148}
                    disabled={edit.limitPerPerson === null}
                    ariaLabel="จำกัดกี่ครั้งต่อคน"
                  />
                  <button
                    type="button"
                    onClick={() => setEdit({ ...edit, limitPerPerson: edit.limitPerPerson === null ? 1 : null })}
                    className={secondaryBtnCls}
                  >
                    {edit.limitPerPerson === null ? 'ตั้งเพดาน' : 'ไม่จำกัด'}
                  </button>
                </span>
              </FormRow>
              )}

              <FormRow
                label="เงื่อนไขการใช้"
                required
                help="ข้อความนี้ลูกค้าเห็นก่อนกดยืนยันการแลก และใช้อ้างอิงเมื่อมีข้อโต้แย้ง — เขียนให้ครบและอ่านรู้เรื่อง"
              >
                <textarea
                  value={edit.conditions}
                  onChange={(e) => setEdit({ ...edit, conditions: e.target.value })}
                  className={textareaCls}
                  placeholder={edit.type === 'product'
                    ? 'เช่น จัดส่งภายใน 7 วันทำการ เฉพาะที่อยู่ในประเทศไทย'
                    : 'เช่น ใช้ได้เมื่อซื้อครบ 800 บาท ใช้ร่วมกับส่วนลดอื่นไม่ได้'}
                />
              </FormRow>

              {shows('imageUpload') ? (
                <ImageDropzone
                  /* ⚠️ ต้องส่ง `file` กลับเข้าไปด้วย — กล่องอัปโหลด spread ค่านี้ตอนแก้คำอธิบายภาพ
                     ถ้าไม่ส่ง ไฟล์ที่เลือกไว้จะหายทันทีที่ผู้ใช้พิมพ์คำอธิบาย แล้วไม่มีอะไรถูกอัปโหลด */
                  value={edit.imageUrl ? { url: edit.imageUrl, alt: edit.imageAlt, file: edit.imageFile } : null}
                  onChange={(v: ImageValue | null) => setEdit({
                    ...edit,
                    imageUrl: v?.url ?? null,
                    imageAlt: v?.alt ?? '',
                    /* เก็บไฟล์ไว้ให้ชั้นข้อมูลอัปโหลดหลังบันทึกสำเร็จ */
                    imageFile: v?.file,
                  })}
                  spec={IMAGE_SPEC}
                  label="ภาพของรางวัล"
                  previewWidth={220}
                />
              ) : edit.imageUrl && (
                <Field label="ภาพของรางวัล" hint="เปลี่ยนภาพจากหน้านี้ยังไม่ได้">
                  <img src={edit.imageUrl} alt={edit.imageAlt} className="w-[220px] rounded-[var(--radius-sm)] object-cover" />
                </Field>
              )}

              <label className="flex cursor-pointer items-start gap-[var(--space-2)] rounded-[var(--radius-sm)] bg-[var(--blue-050)] p-[var(--space-25)]">
                <input
                  type="checkbox"
                  checked={edit.enabled}
                  onChange={(e) => setEdit({ ...edit, enabled: e.target.checked })}
                  className="mt-[3px] h-4 w-4 shrink-0 accent-[var(--blue-700)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
                />
                <span>
                  <span className="block text-[length:var(--text-form-lg)] text-[var(--ink-900)]">เปิดให้แลกในแอป</span>
                  <span className="block text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-700)]">
                    ปิดไว้ = ของรางวัลยังอยู่ในระบบแต่ไม่แสดงในแคตตาล็อก ลูกค้าที่แลกไปแล้วยังใช้สิทธิ์ได้ปกติ
                  </span>
                </span>
              </label>

              {shows('redeemedTotal') && edit.redeemedTotal > 0 && (
                <div className="grid grid-cols-2 gap-[var(--space-3)] border-t border-[var(--line-200)] pt-[var(--space-3)]">
                  <Field label="แลกไปแล้ว">
                    <span className="bo-num" style={{ textAlign: 'left' }}>{edit.redeemedTotal.toLocaleString('th-TH')} ครั้ง</span>
                  </Field>
                  <Field label="คะแนนที่ถูกใช้ไปกับของรางวัลนี้">
                    <span className="bo-num" style={{ textAlign: 'left' }}>{(edit.redeemedTotal * edit.points).toLocaleString('th-TH')} คะแนน</span>
                  </Field>
                </div>
              )}
            </div>
          );
        })()}
        {auditEntries && (
          <div className="mt-[var(--space-6)] border-t border-[var(--line-200)] pt-[var(--space-4)]">
            <h3 className="font-thai text-section leading-heading mb-[var(--space-3)] font-[var(--weight-semibold)] text-[var(--ink-900)]">
              ประวัติการแก้ไขของรางวัล
            </h3>
            {/* บันทึกอัตโนมัติจาก AuditLog ของ backend — ครอบคลุมทุกของรางวัลใน division
                ไม่ใช่เฉพาะชิ้นที่เปิดอยู่ เพราะ endpoint กรองรายชิ้นไม่ได้ */}
            <AuditTrail entries={auditEntries} />
          </div>
        )}
      </SideDrawer>

      <ConfirmDialog
        open={Boolean(confirmSave)}
        tone={confirmSave?.enabled ? 'danger' : 'default'}
        title="บันทึกของรางวัล"
        description={confirmSave ? (
          <>
            <p className="m-0 mb-[var(--space-2)]">
              {confirmSave.name} · {REWARD_TYPE_LABEL[confirmSave.type]} ·{' '}
              <strong className="bo-num">{confirmSave.points.toLocaleString('th-TH')}</strong> คะแนน
              {confirmSave.stock !== null && ` · สต๊อก ${confirmSave.stock.toLocaleString('th-TH')} ชิ้น`}
            </p>
            {confirmSave.enabled && (
              <p className="m-0 font-[var(--weight-medium)] text-[var(--ink-900)]">
                เปิดให้แลก — ของรางวัลนี้จะขึ้นในแคตตาล็อกทันทีที่บันทึก
              </p>
            )}
          </>
        ) : ''}
        confirmLabel="บันทึก"
        requireReason
        reasonLabel="เหตุผลที่แก้"
        reasonPresets={[
          'เติมสต๊อกตามที่ฝ่ายคลังแจ้ง',
          'ปรับคะแนนที่ใช้แลกตามต้นทุนใหม่',
          'ปิดไม่ให้แลกเพราะของหมด',
        ]}
        onConfirm={(reason) => { if (confirmSave) void save(confirmSave, reason); }}
        onCancel={() => setConfirmSave(null)}
      />
    </div>
  );
};

export default RewardCatalogScreen;

/* ============================================================================
   รองรับ:
   FR-ADM-22   จัดการแคตตาล็อกของรางวัลและสต๊อกของรางวัล
   FR-RD-01    แสดงคะแนนที่ต้องใช้ของแต่ละของรางวัล
   FR-RD-02    สามประเภท: สินค้าจริง · คูปองส่วนลด/E-Voucher · สิทธิ์ฟรีค่าจัดส่ง
   FR-RD-04    ตรวจสต๊อกก่อนยืนยันการแลก — หน้านี้คือที่ตั้งค่าสต๊อกและการเตือน
   FR-RD-05    สถานะการจัดส่งของรางวัลที่เป็นสินค้าจริง (หน้าจอติดตามอยู่ในกลุ่มถัดไป)
   FR-ADM-18   สต๊อกและการเตือนเมื่อสต๊อกต่ำ
   FR-RPT-07   รายงานการแลกของรางวัลและการใช้คูปอง
   FR-ADM-37   ทุกการแก้ไขเข้า Audit Log พร้อมเหตุผล

   ประเด็นค้าง (⛔):
   — สต๊อกของรางวัลตัดจากสต๊อกสินค้าเพื่อการขายหรือแยกล็อต ต้องยืนยันกับฝ่ายคลัง
   — เมื่อของรางวัลหมดสต๊อกขณะมีคนแลกค้างอยู่ ต้องคืนคะแนนอัตโนมัติหรือให้เจ้าหน้าที่คืน
   — คะแนนที่ใช้แลกของแต่ละชิ้นยังไม่ได้เทียบกับต้นทุนจริงของของรางวัล
     ตัวเลข "เท่ากับยอดซื้อ" ช่วยตรวจความสมเหตุสมผลได้ แต่ไม่ใช่การคำนวณกำไรขาดทุน
   — ยังไม่มีการจำกัดของรางวัลตามระดับสมาชิก (เช่น ของบางชิ้นเฉพาะ Platinum)
     ถ้าต้องมี ให้เพิ่มช่องกลุ่มเป้าหมายแบบเดียวกับหน้าสิทธิพิเศษ
   — ขนาดภาพ 1:1 ขั้นต่ำ 800px เป็นค่าที่ทีมออกแบบกำหนด ยังไม่ได้ตรวจกับภาพจริงของลูกค้า
   ============================================================================ */
